import type { PermissionCode } from "@/features/auth/permissions";
import type { AdminRole } from "@/features/auth/types";

export type NavigationPermissionRule = {
    anyOf?: readonly PermissionCode[];
    allOf?: readonly PermissionCode[];
    roles?: readonly AdminRole[];
};

export const SECTION_PERMISSIONS = {
    DASHBOARD: ["DASHBOARD_VIEW"] as const,
    USERS: ["USERS_VIEW"] as const,
    USERS_MAP: ["USERS_MAP_VIEW"] as const,
    USERS_EXPORT: ["USERS_EXPORT"] as const,
    USERS_MODERATE: ["USERS_MODERATE"] as const,
    USERS_DELETE: ["USERS_DELETE"] as const,
    RIDERS: ["RIDERS_VIEW"] as const,
    RIDERS_EXPORT: ["RIDERS_EXPORT"] as const,
    RIDER_BOOKINGS: ["RIDER_BOOKINGS_VIEW"] as const,
    RIDER_TRIPS: ["RIDER_TRIPS_VIEW"] as const,
    RIDER_WALLET: ["RIDER_WALLET_VIEW"] as const,
    DRIVERS: ["DRIVERS_VIEW"] as const,
    DRIVERS_EXPORT: ["DRIVERS_EXPORT"] as const,
    DRIVERS_MODERATE: ["DRIVERS_MODERATE"] as const,
    DRIVER_RIDES: ["DRIVER_RIDES_VIEW"] as const,
    DRIVER_TRIPS: ["DRIVER_TRIPS_VIEW"] as const,
    DRIVER_WALLET: ["DRIVER_WALLET_VIEW"] as const,
    DRIVER_ROAD_WORTHINESS_REVIEW: ["DRIVER_ROAD_WORTHINESS_REVIEW"] as const,
    DRIVER_PAYOUTS_VIEW: ["DRIVER_PAYOUTS_VIEW"] as const,
    DRIVER_PAYOUTS_MANAGE: ["DRIVER_PAYOUTS_MANAGE"] as const,
    RIDE_OFFERS: ["RIDE_OFFERS_VIEW"] as const,
    RIDE_OFFERS_MANAGE: ["RIDE_OFFERS_MANAGE"] as const,
    RIDE_BOOKINGS: ["RIDE_BOOKINGS_VIEW"] as const,
    RIDE_BOOKINGS_MANAGE: ["RIDE_BOOKINGS_MANAGE"] as const,
    RIDE_BOOKINGS_REFUND: ["RIDE_BOOKINGS_REFUND"] as const,
    TRIPS: ["TRIPS_VIEW"] as const,
    TRIPS_MANAGE: ["TRIPS_MANAGE"] as const,
    WALLETS: ["WALLETS_VIEW"] as const,
    WALLET_ADJUST: ["WALLET_ADJUST"] as const,
    WALLET_FREEZE: ["WALLET_FREEZE"] as const,
    TRANSACTIONS: ["TRANSACTIONS_VIEW"] as const,
    PAYMENTS: ["PAYMENTS_VIEW"] as const,
    PLATFORM_WALLET: ["PLATFORM_WALLET_VIEW"] as const,
    PAYOUTS_MANAGE: ["PAYOUTS_MANAGE"] as const,
    WITHDRAWALS: ["WITHDRAWALS_VIEW"] as const,
    WITHDRAWALS_MANAGE: ["WITHDRAWALS_MANAGE"] as const,
    WALLET_OPERATIONS: ["WALLET_OPERATIONS_VIEW"] as const,
    WALLET_OPERATIONS_MANAGE: ["WALLET_OPERATIONS_MANAGE"] as const,
    MESSAGING: ["MESSAGING_VIEW"] as const,
    MESSAGING_MANAGE: ["MESSAGING_MANAGE"] as const,
    PUSH_NOTIFICATIONS: ["PUSH_NOTIFICATIONS_SEND"] as const,
    ANALYTICS: ["ANALYTICS_VIEW"] as const,
    AUDIT: ["AUDIT_VIEW"] as const,
    AUDIT_SECURITY: ["AUDIT_SECURITY_VIEW"] as const,
    AUDIT_EXPORT: ["AUDIT_EXPORT"] as const,
    APPROVALS: ["APPROVALS_VIEW"] as const,
    APPROVALS_EXPORT: ["APPROVALS_EXPORT"] as const,
    APPROVALS_MANAGE: ["APPROVALS_MANAGE"] as const,
    FRAUD: ["FRAUD_VIEW"] as const,
    FRAUD_MANAGE: ["FRAUD_MANAGE"] as const,
    FRAUD_RUN_JOBS: ["FRAUD_RUN_JOBS"] as const,
    LIVE_MONITORING: ["LIVE_MONITORING_VIEW"] as const,
} as const;

export const NAVIGATION_PERMISSION_RULES: Record<string, NavigationPermissionRule> = {
    "/": { anyOf: SECTION_PERMISSIONS.DASHBOARD },
    "/users": { anyOf: SECTION_PERMISSIONS.USERS },
    "/riders": { anyOf: SECTION_PERMISSIONS.RIDERS },
    "/drivers": { anyOf: SECTION_PERMISSIONS.DRIVERS },
    "/ride-bookings-trips": {
        anyOf: [
            ...SECTION_PERMISSIONS.RIDE_OFFERS,
            ...SECTION_PERMISSIONS.RIDE_BOOKINGS,
            ...SECTION_PERMISSIONS.TRIPS,
        ],
    },
    "/wallet-payments": {
        anyOf: [
            ...SECTION_PERMISSIONS.WALLETS,
            ...SECTION_PERMISSIONS.TRANSACTIONS,
            ...SECTION_PERMISSIONS.PAYMENTS,
            ...SECTION_PERMISSIONS.PLATFORM_WALLET,
        ],
    },
    "/notifications": { anyOf: SECTION_PERMISSIONS.MESSAGING },
    "/approval-queue": { anyOf: SECTION_PERMISSIONS.APPROVALS },
    "/admin-management": { roles: ["SUPER_ADMIN"] },
    "/platform-configuration": { roles: ["SUPER_ADMIN"] },
    "/reports-analytics": { anyOf: SECTION_PERMISSIONS.ANALYTICS },
    "/audit-logs": {
        anyOf: [...SECTION_PERMISSIONS.AUDIT, ...SECTION_PERMISSIONS.AUDIT_SECURITY],
    },
    "/settings": {},
    "/approvals": { anyOf: SECTION_PERMISSIONS.APPROVALS },
    "/fraud-safety": { anyOf: SECTION_PERMISSIONS.FRAUD },
    "/live-monitoring": { anyOf: SECTION_PERMISSIONS.LIVE_MONITORING },
};
